import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, FileSearch, Clock, Languages, ArrowRight, FileText, Package, MessageCircle, Phone, BadgeCheck, Zap, Lock } from "lucide-react";
import api from "../api";
import { useApp } from "../context/AppContext";
import { Seo } from "../components/Seo";
import { SearchBox } from "../components/SearchBox";
import { Button } from "../components/ui/button";
import { waLink } from "../components/LeadCta";
import { track } from "../analytics";

const HOME_SEO = {
  en: {
    title: "GARVI 2.0, Index-2 & E-Stamping | Gujarat",
    description: "Get GARVI 2.0, Index-2, certified copy, e-stamping and rent agreement maker help in Gujarat. Contact DocGalaxy today.",
  },
  gu: {
    title: "GARVI 2.0, Index-2 & E-Stamping | ગુજરાત",
    description: "ગુજરાતમાં GARVI 2.0, Index-2, Certified Copy, e-stamping અને rent agreement maker સહાય માટે આજે DocGalaxyનો સંપર્ક કરો.",
  },
};

const FAQS = [
  [
    "GARVI 2.0 માં Index-2 કેવી રીતે મેળવવું?",
    "GARVI 2.0 અથવા Garvi પ્રક્રિયામાં Index-2/index2 માટે SRO, દસ્તાવેજ નંબર અને વર્ષ જરૂરી હોય છે. યોગ્ય વિગતો સાથે DocGalaxy માર્ગદર્શન આપી શકે છે.",
    "How do I get an Index-2 through GARVI 2.0?",
    "For a GARVI 2.0 or Garvi Index-2/index2 request, keep the SRO, document number and registration year ready. DocGalaxy can guide you with the right details.",
  ],
  [
    "Index-2 અને Certified Copy માં શું ફરક છે?",
    "Index-2 રજિસ્ટર્ડ દસ્તાવેજનો સાર છે, જ્યારે Certified Copy સમગ્ર દસ્તાવેજની પ્રમાણિત નકલ છે. ઉપયોગ મુજબ યોગ્ય દસ્તાવેજ પસંદ કરવામાં અમારી ટીમ મદદ કરે છે.",
    "What is the difference between Index-2 and a Certified Copy?",
    "Index-2 is a summary of a registered document, while a Certified Copy is the full attested deed. Our team helps you choose the right document for your purpose.",
  ],
  [
    "E-Stamping અથવા estamping માટે કઈ મદદ મળે છે?",
    "E-Stamping (estamping) માટે દસ્તાવેજનો પ્રકાર, પક્ષકારોની વિગતો અને જરૂરી stamp value સમજવી જરૂરી છે. DocGalaxy ગુજરાતમાં પ્રક્રિયા સમજવામાં મદદ કરે છે.",
    "What help is available for E-Stamping or estamping?",
    "For E-Stamping (estamping), the document type, party details and required stamp value matter. DocGalaxy helps explain the Gujarat process.",
  ],
  [
    "Rent agreement maker દ્વારા ભાડા કરાર કેવી રીતે બનાવવો?",
    "Rent agreement maker માટે મકાનમાલિક, ભાડૂત, મિલકત, ભાડું અને સમયગાળાની વિગતો રાખો. અમે ગુજરાત માટે ભાડા કરાર તૈયાર કરવાની પ્રક્રિયામાં મદદ કરીએ છીએ.",
    "How does a rent agreement maker prepare a rental agreement?",
    "Keep the owner, tenant, property, rent and term details ready for a rent agreement maker. We help with the Gujarat rent agreement preparation process.",
  ],
  [
    "રાજકોટ અને ગુજરાત માટે દસ્તાવેજ સહાય કેવી રીતે મેળવવી?",
    "GARVI 2.0, Index-2, Certified Copy, E-Stamping અને rent agreement maker સેવા માટે WhatsApp પર તમારી મિલકતની વિગતો મોકલો. અમારી ટીમ આગળની પ્રક્રિયા સમજાવશે.",
    "How do I get document assistance in Rajkot and Gujarat?",
    "For GARVI 2.0, Index-2, Certified Copy, E-Stamping or a rent agreement maker, send your property details on WhatsApp. Our team will explain the next steps.",
  ],
];

function TrustBadges({ gu }) {
  const items = [
    [BadgeCheck, gu ? "ઓરિજિનલ સરકારી રેકોર્ડ સહાય" : "Original Government Record Assistance"],
    [Zap, gu ? "ઝડપી ડોક્યુમેન્ટ સહાય (ઘણા કેસ 24 કલાકમાં*)" : "Fast Document Assistance (many within 24 hrs*)"],
    [Lock, gu ? "સુરક્ષિત અને ખાનગી પ્રક્રિયા" : "Safe & Secure Process"],
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid="trust-badges">
      {items.map(([Icon, label], i) => (
        <span key={i} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-border rounded-full px-3 py-1.5 text-foreground/80" data-testid={`trust-badge-${i}`}>
          <Icon size={14} className="text-secondary" /> {label}
        </span>
      ))}
    </div>
  );
}

function WhatsAppCTA({ wa, gu, placement, size = "lg" }) {
  if (!wa) return null;
  const msg = gu
    ? "નમસ્તે DocGalaxy, મને Index 2, Certified Copy, Garvi 2.0, E-stamping અથવા Rent Agreement માટે સહાય જોઈએ છે."
    : "Hi DocGalaxy, I need help with Index 2, Certified Copy, Garvi 2.0, E-stamping or a Rent Agreement.";
  const cls = size === "lg"
    ? "h-14 px-7 text-base md:text-lg w-full sm:w-auto"
    : "h-12 px-6 text-sm";
  return (
    <a href={waLink(wa, msg)} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { placement })}
      className={`inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 active:scale-[0.98] text-white font-bold shadow-lg shadow-green-600/25 transition-all ${cls}`}
      data-testid={`whatsapp-cta-${placement}`}>
      <MessageCircle size={size === "lg" ? 24 : 18} /> {gu ? "WhatsApp પર Document Details મોકલો" : "Send Document Details on WhatsApp"}
    </a>
  );
}

export default function Home() {
  const { t, lang, site } = useApp();
  const gu = lang === "gu";
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [packs, setPacks] = useState([]);
  const wa = site.whatsapp;
  const homeSeo = gu ? HOME_SEO.gu : HOME_SEO.en;

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/packs").then((r) => setPacks(r.data)).catch(() => {});
  }, []);

  const name = (s) => (lang === "gu" && s.name_gu ? s.name_gu : s.name_en);
  const origin = typeof window !== "undefined" ? window.location.origin : "https://index2.docgalaxy.in";
  const index2 = services.find((s) => s.slug === "index-2");
  const ccopy = services.find((s) => s.slug === "certified-copy");

  const schema = [
    {
      "@context": "https://schema.org", "@type": "Service", "@id": `${origin}/#index2-service`,
      name: "GARVI 2.0, Index-2 and E-Stamping Assistance",
      serviceType: "GARVI 2.0, Index-2, Certified Copy, E-Stamping and rent agreement maker guidance",
      description: "Gujarat assistance for GARVI 2.0, Index-2, Certified Copy, E-Stamping and rent agreement maker services. Private document assistance service.",
      url: `${origin}/`, provider: { "@id": `${origin}/#organization` },
      areaServed: [{ "@type": "City", name: "Rajkot" }, { "@type": "State", name: "Gujarat" }, { "@type": "Country", name: "India" }],
      availableChannel: { "@type": "ServiceChannel", serviceUrl: `${origin}/order/new/index-2`, availableLanguage: ["gu", "en"] },
    },
  ];

  return (
    <div data-testid="home-page">
      <Seo
        rawTitle={homeSeo.title}
        description={homeSeo.description}
        canonicalPath="/"
      />
      <script type="application/ld+json">{JSON.stringify(schema)}</script>

      <section className="bg-gradient-to-b from-primary/5 via-white to-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 pt-10 pb-10 md:pt-16">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-bold mb-4 fade-up">
              {gu ? "રાજકોટ-ફર્સ્ટ · ગુજરાતના તમામ 33 જિલ્લા" : "Rajkot-first · All 33 Gujarat districts"}
            </span>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight text-foreground fade-up fade-up-1" data-testid="hero-headline">
              {gu
                ? "ગુજરાતમાં GARVI 2.0, Index-2 અને Certified Copy માટે ઓનલાઈન સહાય"
                : "GARVI 2.0, Index-2 & Certified Copy Help in Gujarat"}
            </h1>
            <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-2xl fade-up fade-up-2" data-testid="hero-subheading">
              {gu
                ? "GARVI 2.0 અને Garvi માર્ગદર્શન, Index-2/index2, Certified Copy, E-Stamping (estamping) અને rent agreement maker માટે WhatsApp પર સહાય મેળવો."
                : "Get WhatsApp help for GARVI 2.0 and Garvi guidance, Index-2/index2, Certified Copy, E-Stamping (estamping) and a rent agreement maker."}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 fade-up fade-up-3">
              <WhatsAppCTA wa={wa} gu={gu} placement="hero" />
              <Button size="lg" variant="outline" className="h-14 sm:h-12 px-6 border-primary text-primary hover:bg-primary/5 text-base" onClick={() => navigate("/order/new/index-2")} data-testid="hero-index2-order-btn">
                {gu ? "Index 2 Request Form" : "Index 2 Request Form"} <ArrowRight size={18} className="ml-1" />
              </Button>
            </div>
            <div className="mt-5 fade-up fade-up-4"><TrustBadges gu={gu} /></div>
            <p className="mt-3 text-[11px] text-muted-foreground max-w-2xl" data-testid="hero-disclaimer">
              {gu
                ? "*ડિલિવરી સમય SRO પર રેકોર્ડની ઉપલબ્ધતા અને કામકાજના દિવસો પર આધારિત છે. DocGalaxy એક ખાનગી દસ્તાવેજ સહાય સેવા છે — સરકારી વિભાગ કે પોર્ટલ નથી."
                : "*Delivery time depends on record availability at the SRO and working days. DocGalaxy is a private document assistance service — not a government department or portal."}
            </p>
          </div>
          <div className="mt-10 fade-up fade-up-4">
            <SearchBox />
          </div>
        </div>
      </section>

      {site.announcement && (
        <div className="bg-accent text-accent-foreground text-center text-sm py-2.5 px-4 font-medium" data-testid="announcement-bar">
          {site.announcement}
        </div>
      )}

      <section className="max-w-7xl mx-auto px-4 py-14" data-testid="benefits-section">
        <h2 className="font-display font-bold text-2xl md:text-3xl">
          {gu
            ? "GARVI 2.0 અને Index-2 દસ્તાવેજ સેવાઓના ફાયદા"
            : "Benefits of GARVI 2.0 and Index-2 Document Assistance"}
        </h2>
        <p className="text-muted-foreground mt-1 mb-8">
          {gu
            ? "GARVI 2.0, Index-2, Certified Copy અને estamping માટે DocGalaxy કેમ?"
            : "Why choose DocGalaxy for GARVI 2.0, Index-2, Certified Copy and estamping in Gujarat?"}
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            [Clock, gu ? "કચેરીના ધક્કા નહીં" : "No office visits", gu ? "ઘરેથી WhatsApp પર વિગતો મોકલો; PDF WhatsApp/ડેશબોર્ડ પર મેળવો." : "Send details from home on WhatsApp; receive the PDF on WhatsApp/dashboard."],
            [BadgeCheck, gu ? "ઓરિજિનલ રેકોર્ડ" : "Original record", gu ? "SRO ના અધિકૃત રેકોર્ડમાંથી કાયદેસર માર્ગે મેળવેલ Index 2 / Certified Copy." : "Index 2 / Certified Copy obtained lawfully from official SRO records."],
            [Zap, gu ? "ઝડપી ડિલિવરી" : "Fast delivery", gu ? "ઘણા request કામકાજના 24 કલાકમાં પૂરા; સ્ટેટસ લાઇવ ટ્રેક કરો." : "Many requests completed within 24 working hours; live status tracking."],
            [Languages, gu ? "ગુજરાતી સપોર્ટ" : "Gujarati support", gu ? "રાજકોટ ટીમ ગુજરાતી + English માં કોલ/WhatsApp પર માર્ગદર્શન આપે છે." : "Rajkot team guides you in Gujarati + English on call/WhatsApp."],
          ].map(([Icon, h, p], i) => (
            <div key={i} className="bg-white border border-border rounded-xl p-5 card-hover">
              <span className="w-10 h-10 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center"><Icon size={20} /></span>
              <h3 className="font-display font-semibold mt-3">{h}</h3>
              <p className="text-sm text-muted-foreground mt-1">{p}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 border-y border-border" data-testid="required-details-section">
        <div className="max-w-7xl mx-auto px-4 py-14 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-display font-bold text-2xl md:text-3xl">જરૂરી વિગતો</h2>
            <p className="text-muted-foreground mt-1">{gu ? "Index 2 copy Rajkot / Gujarat અથવા Certified Copy માટે WhatsApp પર આ વિગતો મોકલો:" : "Send these details on WhatsApp for an Index 2 copy or Certified Copy in Rajkot / Gujarat:"}</p>
            <ul className="mt-5 space-y-2.5">
              {[
                gu ? "જિલ્લો અને SRO (સબ-રજિસ્ટ્રાર ઓફિસ) નું નામ — દા.ત. Rajkot-1" : "District and SRO (Sub-Registrar Office) name — e.g. Rajkot-1",
                gu ? "દસ્તાવેજ (Document) નંબર અને રજિસ્ટ્રેશન વર્ષ" : "Document number and registration year",
                gu ? "પક્ષકારનું નામ (વેચનાર / ખરીદનાર) — નંબર ન હોય તો" : "Party name (seller / buyer) — if document number is unknown",
                gu ? "ગામ / શહેર, સર્વે અથવા બ્લોક નંબર (વૈકલ્પિક)" : "Village / city, survey or block number (optional)",
                gu ? "તમારું નામ અને WhatsApp નંબર" : "Your name and WhatsApp number",
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm"><ShieldCheck size={16} className="text-secondary mt-0.5 shrink-0" /> {s}</li>
              ))}
            </ul>
            <div className="mt-6"><WhatsAppCTA wa={wa} gu={gu} placement="details" size="md" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[index2, ccopy].filter(Boolean).map((s) => (
              <div key={s.id} className="bg-white border border-primary/20 rounded-xl p-5 flex flex-col" data-testid={`featured-${s.slug}`}>
                <span className="text-[10px] font-bold uppercase tracking-wide text-secondary">{gu ? "લોકપ્રિય" : "Popular"}</span>
                <h3 className="font-display font-semibold text-lg mt-1">{s.slug === "index-2" ? (gu ? "Index 2 Copy Online — રાજકોટ અને ગુજરાત" : "Index 2 Copy Online — Rajkot & Gujarat") : (gu ? "Certified Copy (વેચાણ દસ્તાવેજ) — રાજકોટ અને ગુજરાત" : "Certified Copy (Sale Deed) — Rajkot & Gujarat")}</h3>
                <p className="text-sm text-muted-foreground mt-1 flex-1">{lang === "gu" && s.desc_gu ? s.desc_gu : s.desc_en}</p>
                <div className="flex items-center justify-between mt-3 text-sm"><span className="font-bold text-primary">₹{s.price}</span><span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} /> {s.eta}</span></div>
                <Button className="mt-3 bg-primary" onClick={() => navigate(`/order/new/${s.slug}`)} data-testid={`featured-request-${s.slug}`}>{t("request_now")}</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-14" data-testid="services-section">
        <h2 className="font-display font-bold text-2xl md:text-3xl">
          {gu
            ? "રાજકોટની દસ્તાવેજ સેવાઓ: GARVI 2.0 થી Rent Agreement Maker સુધી"
            : "Rajkot Services: GARVI 2.0 to Rent Agreement Maker"}
        </h2>
        <p
          className="text-muted-foreground mt-1 mb-8"
          data-testid="rajkot-services-seo-copy"
        >
          {gu
            ? "રાજકોટમાં Index-2/index2, Certified Copy, Garvi 2.0 માર્ગદર્શન, E-Stamping (estamping) અને rent agreement maker માટે ગુજરાતી તથા English માં દસ્તાવેજ સહાય મેળવો."
            : "Get Rajkot-focused Gujarati and English help for Index-2/index2, Certified Copy, Garvi 2.0 guidance, E-Stamping (estamping) and a rent agreement maker."}
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {services.map((s) => (
            <div key={s.id} className="bg-white border border-border rounded-xl p-5 card-hover flex flex-col" data-testid={`service-card-${s.slug}`}>
              <div className="flex items-start justify-between">
                <span className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><FileText size={20} /></span>
                <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-muted text-muted-foreground">
                  {t(s.source_type === "verification" ? "verification_service" : "assistance")}
                </span>
              </div>
              <h3 className="font-display font-semibold mt-3">{name(s)}</h3>
              <p className="text-sm text-muted-foreground mt-1 flex-1 line-clamp-2">{lang === "gu" && s.desc_gu ? s.desc_gu : s.desc_en}</p>
              <div className="flex items-center justify-between mt-4">
                <span className="font-bold text-primary">₹{s.price}</span>
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} /> {s.eta}</span>
              </div>
              <Button className="mt-3 w-full bg-secondary hover:bg-secondary/90" onClick={() => navigate(`/order/new/${s.slug}`)} data-testid={`request-${s.slug}`}>
                {t("request_now")}
              </Button>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 border-y border-border" data-testid="packs-section">
        <div className="max-w-7xl mx-auto px-4 py-14">
          <h2 className="font-display font-bold text-2xl md:text-3xl">{t("packs_title")}</h2>
          <p className="text-muted-foreground mt-1 mb-8">{t("packs_sub")}</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {packs.map((p) => (
              <div key={p.id} className="bg-white rounded-xl border border-border p-6 card-hover flex flex-col" data-testid={`pack-card-${p.slug}`}>
                <Package size={22} className="text-secondary" />
                <h3 className="font-display font-semibold mt-2">{lang === "gu" && p.name_gu ? p.name_gu : p.name_en}</h3>
                <p className="text-sm text-muted-foreground mt-1 flex-1">{p.desc_en}</p>
                <ul className="text-xs text-muted-foreground mt-3 space-y-1">
                  {(p.includes || []).map((i) => <li key={i}>• {i}</li>)}
                </ul>
                <div className="font-bold text-xl text-primary mt-4">₹{p.price}</div>
                <Button className="mt-3 w-full" onClick={() => navigate(`/order/new/pack-${p.slug}`)} data-testid={`pack-request-${p.slug}`}>{t("request_now")}</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-14" data-testid="how-it-works">
        <h2 className="font-display font-bold text-2xl md:text-3xl mb-2">{t("how_it_works")}</h2>
        <p className="text-muted-foreground mb-8">{gu ? "Index 2 / Certified Copy request થી ડિલિવરી સુધી — 4 સરળ પગલાં" : "From Index 2 / Certified Copy request to delivery — 4 simple steps"}</p>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            gu ? "WhatsApp પર Document Details મોકલો અથવા Request Form ભરો" : "Send document details on WhatsApp or fill the request form",
            gu ? "અમારી ટીમ વિગતો ચકાસી કિંમત કન્ફર્મ કરે; સુરક્ષિત ચુકવણી કરો" : "Our team verifies details and confirms price; pay securely",
            gu ? "SRO રેકોર્ડમાંથી કાયદેસર માર્ગે Index 2 / Certified Copy મેળવાય" : "Index 2 / Certified Copy is retrieved lawfully from SRO records",
            gu ? "PDF WhatsApp અને તમારા ડેશબોર્ડ પર ડિલિવર; સ્ટેટસ ટ્રેક કરો" : "PDF delivered on WhatsApp and your dashboard; track status live",
          ].map((s, i) => (
            <div key={i} className="relative bg-white border border-border rounded-xl p-6">
              <span className="font-display text-4xl font-extrabold text-primary/15 absolute top-3 right-4">{i + 1}</span>
              <p className="font-medium mt-4 text-sm">{s}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-primary text-white" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 py-14">
          <h2 className="font-display font-bold text-2xl md:text-3xl mb-8">{t("why_title")}</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              [ShieldCheck, t("why1")], [FileSearch, t("why2")], [Clock, t("why3")], [Languages, t("why4")],
            ].map(([Icon, text], i) => (
              <div key={i} className="bg-white/10 rounded-xl p-6 backdrop-blur">
                <Icon size={26} className="text-emerald-300" />
                <p className="mt-3 font-medium">{text}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-xs text-blue-200 max-w-3xl">
            <strong className="text-white">{t("disclaimer_title")}:</strong> {t("disclaimer_text")}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-14" data-testid="faq-section">
        <h2 className="font-display font-bold text-2xl md:text-3xl">
          {gu ? "GARVI 2.0, Index-2 અને E-Stamping FAQ" : "GARVI 2.0, Index-2 and E-Stamping FAQs"}
        </h2>
        <p className="text-muted-foreground mt-1 mb-6">
          {gu
            ? "GARVI 2.0, Index-2, Certified Copy, estamping અને rent agreement maker વિશે"
            : "About GARVI 2.0, Index-2, Certified Copy, estamping and a rent agreement maker"}
        </p>
        <div className="space-y-3">
          {FAQS.map(([qg, ag, qe, ae], i) => (
            <details key={i} className="bg-white border border-border rounded-xl p-4 group" data-testid={`home-faq-${i}`}>
              <summary className="font-semibold cursor-pointer list-none flex justify-between items-center gap-3">
                <h3 className="text-base font-semibold">{gu ? qg : qe}</h3>
                <span className="text-primary group-open:rotate-45 transition-transform text-xl leading-none shrink-0">+</span>
              </summary>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{gu ? ag : ae}</p>
            </details>
          ))}
        </div>
        <p className="text-sm text-muted-foreground mt-6">
          {gu ? "વધુ વાંચો: " : "Read more: "}
          <Link to="/blog/index-2-certified-copy-difference" className="text-primary font-semibold">{gu ? "Index-2 vs Certified Copy" : "Index-2 vs Certified Copy"}</Link> ·{" "}
          <Link to="/blog/index-2-search-certified-copy-rajkot-guide" className="text-primary font-semibold">{gu ? "Index 2 search & certified copy Rajkot guide" : "Index 2 search & certified copy Rajkot guide"}</Link> ·{" "}
          <Link to="/blog/garvi-2-guide-gujarat" className="text-primary font-semibold">Garvi 2.0 guide</Link> ·{" "}
          <Link to="/rajkot-property-documents" className="text-primary font-semibold">{gu ? "રાજકોટ પ્રોપર્ટી દસ્તાવેજો" : "Rajkot property documents"}</Link>
        </p>
      </section>

      <section className="bg-slate-50 border-t border-border" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 py-14 text-center">
          <h2 className="font-display font-bold text-2xl md:text-3xl">{gu ? "Index 2 / Certified Copy આજે જ મેળવો" : "Get your Index 2 / Certified Copy today"}</h2>
          <p className="text-muted-foreground mt-2">{gu ? "SRO, દસ્તાવેજ નંબર અને વર્ષ WhatsApp પર મોકલો — બાકીનું અમે સંભાળીશું." : "Send SRO, document number and year on WhatsApp — we handle the rest."}</p>
          <div className="mt-6 flex flex-col sm:flex-row justify-center items-center gap-3">
            <WhatsAppCTA wa={wa} gu={gu} placement="footer" />
            {site.phone && (
              <a href={`tel:${site.phone}`} onClick={() => track("phone_click", { placement: "home_footer" })} className="inline-flex items-center gap-2 h-12 px-6 rounded-xl border border-primary text-primary font-semibold text-sm hover:bg-primary/5" data-testid="home-call-btn">
                <Phone size={18} /> {gu ? "કોલ કરો" : "Call"} {site.phone}
              </a>
            )}
            <Button size="lg" variant="outline" className="h-12 px-6" asChild data-testid="cta-contact"><Link to="/contact">{t("contact")}</Link></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
